import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Close as CloseIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import logger from '../utils/logger';

const SubscriptionPromptModal = ({ open, onClose, userStatus }) => {
  const { refreshSubscriptionData, logout } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState({
    plans: [],
    loading: false
  });
  const [subscriptionActionLoading, setSubscriptionActionLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (open) {
      loadSubscriptionPlans();
    }
  }, [open]);

  const loadSubscriptionPlans = async () => {
    setSubscriptionData(prev => ({ ...prev, loading: true }));
    try {
      const response = await api.get('/api/subscription/plans');
      if (response.data.success) {
        setSubscriptionData({
          plans: Array.isArray(response.data.data?.plans) ? response.data.data.plans : [],
          loading: false
        });
      }
    } catch (error) {
      logger.error('Error loading plans:', error);
      setSubscriptionData({ plans: [], loading: false });
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscriptionActionLoading(true);
    try {
      const response = await api.post('/api/subscription/activate', {
        plan_id: planId
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Subscription activated successfully!' });
        // Trigger global subscription refresh
        if (refreshSubscriptionData) {
          await refreshSubscriptionData();
        }
        setTimeout(() => {
          onClose(true); // Pass true to indicate successful subscription
        }, 1500);
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to activate subscription' });
      }
    } catch (error) {
      logger.error('Error subscribing:', error);
      setMessage({ type: 'error', text: 'An error occurred while activating subscription' });
    } finally {
      setSubscriptionActionLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      onClose(false); // Close modal after logout
    } catch (error) {
      logger.error('Error during logout:', error);
      setMessage({ type: 'error', text: 'An error occurred during logout' });
    }
  };

  const getModalContent = () => {
    if (!userStatus) return null;

    const { status, days_remaining, is_cancelled_but_active } = userStatus;

    switch (status) {
      case 'trial':
        return {
          icon: <AccessTimeIcon color="warning" sx={{ fontSize: 40 }} />,
          title: 'Your Trial is Running Out!',
          subtitle: `${days_remaining} days remaining`,
          message: days_remaining <= 3 
            ? 'Your trial expires soon! Upgrade now to continue enjoying all premium features.'
            : 'You\'re currently on a trial period. Upgrade to a paid plan for continued access to all features.',
          color: days_remaining <= 3 ? 'error' : 'warning',
          canClose: days_remaining > 3
        };
      case 'cancelled':
        if (is_cancelled_but_active) {
          return {
            icon: <WarningIcon color="warning" sx={{ fontSize: 40 }} />,
            title: 'Your Subscription is Ending Soon',
            subtitle: `Access ends in ${days_remaining} days`,
            message: `Your subscription was cancelled but you still have access for ${days_remaining} more days. Reactivate your subscription to continue enjoying all features without interruption.`,
            color: days_remaining <= 3 ? 'error' : 'warning',
            canClose: days_remaining > 3
          };
        } else {
          return {
            icon: <CancelIcon color="error" sx={{ fontSize: 40 }} />,
            title: 'Subscription Required',
            subtitle: 'No active subscription',
            message: 'Your subscription has ended. Subscribe now to regain access to all premium features and continue using the platform.',
            color: 'error',
            canClose: false
          };
        }
      case 'expired':
        return {
          icon: <CancelIcon color="error" sx={{ fontSize: 40 }} />,
          title: 'Subscription Required',
          subtitle: 'No active subscription',
          message: 'Your trial has expired. Subscribe now to regain access to all premium features and continue using the platform.',
          color: 'error',
          canClose: false
        };
      default:
        return {
          icon: <WarningIcon color="warning" sx={{ fontSize: 40 }} />,
          title: 'Upgrade Your Account',
          subtitle: 'Limited access',
          message: 'Subscribe to unlock all premium features and get the most out of our platform.',
          color: 'warning',
          canClose: true
        };
    }
  };

  const modalContent = getModalContent();
  if (!modalContent) return null;

  return (
    <Dialog
      open={open}
      onClose={modalContent.canClose ? () => onClose(false) : undefined}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={!modalContent.canClose}
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        position: 'relative'
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {modalContent.icon}
          <Typography variant="h5" component="div" fontWeight="bold">
            {modalContent.title}
          </Typography>
          <Chip 
            label={modalContent.subtitle} 
            color={modalContent.color} 
            variant="outlined"
          />
        </Box>
        {modalContent.canClose && (
          <Button
            onClick={() => onClose(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            size="small"
            color="inherit"
          >
            <CloseIcon />
          </Button>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {message.text && (
          <Alert 
            severity={message.type} 
            sx={{ mb: 2 }}
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          {modalContent.message}
        </Typography>

        {subscriptionData.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              Choose Your Plan
            </Typography>
            <Grid container spacing={2}>
              {subscriptionData.plans.map((plan) => (
                <Grid item xs={12} md={6} key={plan.id}>
                  <Card 
                    sx={{ 
                      border: plan.is_featured ? '2px solid' : '1px solid',
                      borderColor: plan.is_featured ? 'primary.main' : 'divider',
                      position: 'relative',
                      height: '100%'
                    }}
                  >
                    {plan.is_featured && (
                      <Box sx={{ 
                        position: 'absolute', 
                        top: -10, 
                        right: 10, 
                        backgroundColor: 'primary.main', 
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        <StarIcon fontSize="small" />
                        <Typography variant="caption">Popular</Typography>
                      </Box>
                    )}
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Typography variant="h6" gutterBottom>
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                        {plan.description}
                      </Typography>
                      <Typography variant="h4" sx={{ mb: 2 }}>
                        €{plan.price_eur}
                        <Typography component="span" variant="body2" color="text.secondary">
                          /{plan.billing_period}
                        </Typography>
                      </Typography>
                      
                      {/* Plan Features */}
                      <List dense sx={{ mb: 2 }}>
                        {plan.max_lusha_credits && (
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={`${plan.max_lusha_credits} Lusha credits/month`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        )}
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Full platform access"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="AI-powered research tools"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </List>

                      <Button
                        variant={plan.is_featured ? 'contained' : 'outlined'}
                        fullWidth
                        disabled={subscriptionActionLoading}
                        onClick={() => handleSubscribe(plan.id)}
                        sx={{ mt: 'auto' }}
                      >
                        {subscriptionActionLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          'Subscribe Now'
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center' }}>
        {modalContent.canClose && (
          <Button 
            onClick={() => onClose(false)} 
            disabled={subscriptionActionLoading}
            color="inherit"
          >
            Maybe Later
          </Button>
        )}
        {!modalContent.canClose && (
          <Button 
            onClick={handleLogout}
            disabled={subscriptionActionLoading}
            color="inherit"
            variant="outlined"
            startIcon={<LogoutIcon />}
          >
            Log Out
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionPromptModal; 